import { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store/store';
import {
  fetchCommoditiesNonRecursive,
  fetchCommodityById,
  updateCommodity,
} from '../../../store/tradeAPI/commodity/reducers/commodity.slice';
import { CommoditiesAddTemplate } from '../../templates';
import { CommodityUpdateRequestDto } from '../../../store/tradeAPI/commodity/dto/request/commodity-update-request.dto';
import Spinner from '../../atoms/Spinner/Spinner';
import { NonRecursiveCommodityResponseDto } from '../../../store/tradeAPI/commodity/dto/response/non-recursive-commodity-response.dto';
import { ImageMeta } from '../../../store/mediaAPI/media/dto/request/media-save-request';
import { MediaSaveResponseDto } from '../../../store/mediaAPI/media/dto/response/media-save-response';
import { saveMedia } from '../../../store/mediaAPI/media/reducers/media.slice';
import {
  getLanguageKey,
  LocalesLanguagesConstants,
} from '../../../locales/locales';
import {
  translateObject,
  TranslateParams,
} from '../../../store/translateAPI/translate/reducers/translate.slice';
import { showSuccess } from '../../../utils/Notifications';

interface CommoditiesEditPageProps {}

const CommoditiesEditPage: FC<CommoditiesEditPageProps> = () => {
  const [commodity, setCommodity] =
    useState<NonRecursiveCommodityResponseDto>();

  const [imageMeta, setImageMeta] = useState<ImageMeta>();

  const { id } = useParams();

  const navigate = useNavigate();

  const { status, error } = useSelector(
    (state: RootState) => state.commoditySlice
  );

  const { status: mediaStatus } = useSelector(
    (state: RootState) => state.mediaSlice
  );

  const dispatch = useDispatch<AppDispatch>();

  const [selectedLanguage, setLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );

  const [fromLanguage, setFromLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );

  const [toLanguage, setToLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );

  const translate = async (toLanguage: LocalesLanguagesConstants) => {
    if (!commodity) return;
    const params: TranslateParams<NonRecursiveCommodityResponseDto> = {
      obj: commodity,
      from: getLanguageKey(fromLanguage)!,
      to: [getLanguageKey(toLanguage)!],
    };

    const translatedResponse = (await dispatch(
      translateObject(params)
    ).unwrap()) as NonRecursiveCommodityResponseDto;

    const updateRequest: CommodityUpdateRequestDto = {
      name: translatedResponse.name,
      parentId: commodity.parentId,
      hsCode: commodity.hsCode,
      qtyUnit: translatedResponse.qtyUnit,
      timeUnit: translatedResponse.timeUnit,
      image: commodity.image,
      id: commodity.id,
      published: commodity.published,
    };
    await dispatch(
      updateCommodity({
        updateRequest,
        language: toLanguage,
      })
    ).unwrap();
    setLanguage(toLanguage);
  };

  const generateAllTranslations = async () => {
    return await Promise.allSettled(
      Object.values(LocalesLanguagesConstants).map(translate)
    );
  };

  const generateTranslation = async () => {
    return await translate(toLanguage);
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchCommodityById({ id: id, language: selectedLanguage }))
        .unwrap()
        .then((response: NonRecursiveCommodityResponseDto) => {
          setCommodity(response);
        });
    }
  }, [id, dispatch, selectedLanguage]);

  const processMedia = async (
    file: string,
    meta: ImageMeta | undefined
  ): Promise<MediaSaveResponseDto> => {
    if (!meta) {
      return { href: file };
    }
    const formData = new FormData();
    formData.append(
      'file',
      new Blob([file], {
        type: meta.imageType ?? 'image/png',
      }),
      meta.imageSrc
    );
    const response = (await dispatch(
      saveMedia(formData)
    ).unwrap()) as MediaSaveResponseDto;
    return response;
  };

  const handleSubmit = async (params: NonRecursiveCommodityResponseDto) => {
    const image = await processMedia(commodity?.image ?? '', imageMeta);
    const commodityToUpdate: CommodityUpdateRequestDto = {
      name: params.name,
      parentId: params.parentId,
      hsCode: params.hsCode,
      qtyUnit: params.qtyUnit,
      timeUnit: params.timeUnit,
      image: image.href,
      id: params.id,
      published: params.published,
    };
    await dispatch(
      updateCommodity({
        updateRequest: commodityToUpdate,
        language: selectedLanguage,
      })
    );
    showSuccess('Commodity saved successfully.');
    dispatch(fetchCommoditiesNonRecursive());
    navigate('/commodities');
  };

  const handleOnChange = (params: NonRecursiveCommodityResponseDto) => {
    setCommodity(params);
  };

  const handleImageOnChange = (imageMeta: ImageMeta) => {
    setImageMeta(imageMeta);
  };

  if (status === 'loading' || mediaStatus === 'loading') {
    return <Spinner />;
  }

  if (status === 'failed') {
    return <div>{error}</div>;
  }

  return (
    <>
      {commodity && (
        <CommoditiesAddTemplate
          handleSubmit={handleSubmit}
          commodity={commodity}
          handleOnChange={handleOnChange}
          handleImageOnChange={handleImageOnChange}
          setLanguage={setLanguage}
          selectedLanguage={selectedLanguage}
          fromLanguage={fromLanguage}
          setFromLanguage={setFromLanguage}
          toLanguage={toLanguage}
          setToLanguage={setToLanguage}
          generateTranslation={generateTranslation}
          generateAllTranslations={generateAllTranslations}
        />
      )}
    </>
  );
};

export default CommoditiesEditPage;
